import { rem } from 'polished'
import React, { useCallback, useContext, useId, useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { buildAccommodationCancellationPolicies, formatDate } from '@luxuryescapes/lib-refunds'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import HelpPane from 'components/Common/HelpPane'
import Group from 'components/utils/Group'
import InlineIcon from 'components/utils/InlineIcon'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaffOrAdmin } from 'selectors/accountSelectors'

/**
 * Default timezone used when property timezone is not available.
 * Using UTC as a neutral default that works across all regions.
 */
const DEFAULT_PROPERTY_TIMEZONE = 'UTC'

const StyledRadioInput = styled(RadioInput)`
  display: flex;
  align-items: start;
`

const CancellationClickable = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  text-decoration: none;
`

interface Props {
  rates: Array<App.PropertyRate>
  selectedRateId: string | undefined
  onChange: (rateId: string) => void
  propertyTimezone: string
}

/**
 * Get cancellation policy display text
 */
interface CancellationDisplayText {
  primary: string
  secondary?: string
  highlightPrimary: boolean
}

function getCancellationText(
  intl: ReturnType<typeof useIntl>,
  rate: App.PropertyRate,
  timezone: string,
): CancellationDisplayText {
  const { headlineCancellationPolicyType, headlineCancellationPolicyStart } = rate

  const nonRefundableLabel = intl.formatMessage({
    id: 'xvWHpDA1+K',
    defaultMessage: 'Non refundable',
    description: 'Cancellation policy label for non-refundable property rates',
  })
  const freeCancellationLabel = intl.formatMessage({
    id: 'NmC8LTH4QQ',
    defaultMessage: 'Free cancellation',
    description: 'Cancellation policy label for fully refundable property rates',
  })
  const partiallyRefundableLabel = intl.formatMessage({
    id: 'ZznnSheC7a',
    defaultMessage: 'Partially refundable',
    description: 'Cancellation policy label for partially refundable property rates',
  })

  if (headlineCancellationPolicyType === 'nonRefundable') {
    return {
      primary: nonRefundableLabel,
      highlightPrimary: false,
    }
  }

  const refundText = headlineCancellationPolicyType === 'fullyRefundable'
    ? freeCancellationLabel
    : partiallyRefundableLabel

  // Format date if available
  if (headlineCancellationPolicyStart) {
    const formattedDate = formatDate.formattedShortDateByTimezone(
      headlineCancellationPolicyStart,
      timezone || DEFAULT_PROPERTY_TIMEZONE,
    )

    return {
      primary: refundText,
      secondary: intl.formatMessage(
        {
          id: 'uO5LMI6zaa',
          defaultMessage: 'until {date}',
          description: 'Date suffix for cancellation policy',
        },
        { date: formattedDate },
      ),
      highlightPrimary: true,
    }
  }

  return {
    primary: refundText,
    highlightPrimary: true,
  }
}

function PropertyRefundabilitySelector(props: Props) {
  const {
    rates,
    selectedRateId,
    onChange,
    propertyTimezone,
  } = props

  const intl = useIntl()
  const radioGroupName = useId()
  const showModal = useContext(ModalContext)

  const canShowStaffLabel = useAppSelector(state => isLEStaffOrAdmin(state) || state.auth.account.isSpoofed)

  const selectedRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) || rates[0]
  }, [rates, selectedRateId])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const rateId = event.target.value
    onChange(rateId)
  }, [onChange])

  const handleShowCancellationDetails = useCallback((rate: App.PropertyRate | undefined, label: string) => {
    if (!rate) {
      return
    }

    const { description: policyDescriptions } = buildAccommodationCancellationPolicies({
      fullRefundableUntil: rate.fullyRefundableUntil,
      cancellationPolicies: rate.cancellationPolicies.map(({
        startIsoDateTime,
        endIsoDateTime,
        penaltyPrice,
        isFullyNonRefundable,
        originalDescription,
      }) => ({
        amount: penaltyPrice,
        percent: 0,
        description: originalDescription,
        start: startIsoDateTime,
        end: endIsoDateTime,
        nonRefundable: isFullyNonRefundable,
      })),
    }, {
      timezone: propertyTimezone,
      currencyCode: rate.currency,
      hideExcludesFlights: !rate.includesFlights,
    })

    showModal(<Modal title={label}>
      {policyDescriptions.map((line, index) => <BodyText variant="large" as="p" key={index}>{line}</BodyText>)}
      <HelpPane />
    </Modal>)
  }, [propertyTimezone, showModal])

  if (rates.length === 0) {
    return null
  }

  return (
    <RadioGroup>
      {rates.map((rate) => {
        const text = getCancellationText(intl, rate, propertyTimezone)
        const isSelected = rate.rateId === selectedRateId
        let staffRateLabel: string | undefined = undefined
        if (canShowStaffLabel && rate.staffLabel) {
          staffRateLabel = intl.formatMessage({
            id: 'HJnIuZ3JW8',
            defaultMessage: '{staffLabel} (label only shows for staff)',
            description: 'Staff-only label providing additional rate details',
          }, { staffLabel: rate.staffLabel })
        }
        // Use luxPlusTotals (membership price) if available, otherwise use regular totals
        const ratePrice = rate.luxPlusTotals?.sellingPrice ?? rate.totals.sellingPrice
        const selectedRatePrice = selectedRate?.luxPlusTotals?.sellingPrice ?? selectedRate?.totals.sellingPrice
        const priceDifference = selectedRate && rate.rateId !== selectedRate.rateId && selectedRatePrice
          ? ratePrice - selectedRatePrice
          : 0

        return (
          <StyledRadioInput
            key={rate.rateId}
            name={radioGroupName}
            value={rate.rateId}
            onChange={handleChange}
            checked={isSelected}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
              <BodyText
                variant="medium"
                weight={text.highlightPrimary ? 'bold' : undefined}
                colour={text.highlightPrimary ? 'highlight-secondary' : undefined}
                wrap="no-wrap"
              >
                {text.primary}
              </BodyText>
              {text.secondary && (
                <BodyText variant="medium" wrap="no-wrap">
                  {text.secondary}
                </BodyText>
              )}
              {staffRateLabel && (
                <BodyText variant="medium" colour="neutral-three">
                  {staffRateLabel}
                </BodyText>
              )}
              {!isSelected && priceDifference > 0 && (
                <BodyText variant="medium" colour="neutral-two" weight="bold" wrap="no-wrap">
                  <FormattedMessage
                    id="mGHDJoYlHa"
                    defaultMessage="+{price}"
                    description="Positive price difference with plus sign for refundability option"
                    values={{ price: <FormatCurrency value={priceDifference} format="roundedDollar" /> }}
                  />
                </BodyText>
              )}
              {!isSelected && priceDifference < 0 && (
                <BodyText variant="medium" colour="neutral-two" weight="bold" wrap="no-wrap">
                  <FormatCurrency value={priceDifference} format="roundedDollar" />
                </BodyText>
              )}
              <CancellationClickable
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  handleShowCancellationDetails(rate, text.primary)
                }}
              >
                <InlineIcon Icon={LineInfoCircleIcon} />
              </CancellationClickable>
            </Group>
          </StyledRadioInput>
        )
      })}
    </RadioGroup>
  )
}

export default PropertyRefundabilitySelector
